// Drawola Studio — view switching (Studio home <-> editor).
//
// Fork-only. No upstream counterpart, so it never conflicts on merge.
//
// Deliberately router-free: the app has exactly two views and upstream owns
// the URL (hash deep links, `history.replaceState` inside `initializeScene`).
// So we keep the URL untouched and put the view in `history.state` instead:
//
//   { drawolaView: "studio" }  <- seeded onto the entry the app booted on
//   { drawolaView: "editor" }  <- pushed when a draw is opened
//
// That gives the browser Back button the expected behaviour (editor -> home)
// without ever inventing a URL that could collide with `#room=` / `#json=` /
// `#addLibrary=`.

import { useCallback, useEffect, useState } from "react";

import { hasEditorDeepLink } from "./deepLinks";

export type StudioView = "studio" | "editor";

type StudioHistoryState = { drawolaView?: StudioView } | null;

const readViewFromHistory = (): StudioView | null => {
  const view = (window.history.state as StudioHistoryState)?.drawolaView;
  return view === "studio" || view === "editor" ? view : null;
};

export type StudioViewApi = {
  view: StudioView;
  /** Show the editor and push a history entry so Back returns to the Studio. */
  openEditor: () => void;
  /** Show the Studio home, unwinding the pushed entry when there is one. */
  openStudio: () => void;
};

/**
 * Owns which of the two views is on screen.
 *
 * @param enabled `DRAWOLA_FEATURES.SHOW_STUDIO_HOME`. When false the hook is
 *   inert and always reports `"editor"`, so the app behaves exactly as it did
 *   before the Studio existed.
 */
export const useStudioView = (enabled: boolean): StudioViewApi => {
  const [view, setView] = useState<StudioView>(() => {
    if (!enabled || hasEditorDeepLink()) {
      return "editor";
    }
    // A reload lands on whatever entry the user was on.
    return readViewFromHistory() ?? "studio";
  });

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    // Seed the boot entry so the very first Back press from the editor has
    // something to land on. Skipped for deep links: `initializeScene()`
    // rewrites history for those and we must not race it.
    if (!hasEditorDeepLink() && readViewFromHistory() === null) {
      window.history.replaceState(
        { ...(window.history.state ?? {}), drawolaView: "studio" },
        "",
      );
    }

    const onPopState = (event: PopStateEvent) => {
      // A hash deep link may have been navigated to (or back to) — upstream's
      // `hashchange` handler takes over from here, so stay in the editor.
      if (hasEditorDeepLink()) {
        setView("editor");
        return;
      }
      const next = (event.state as StudioHistoryState)?.drawolaView;
      // Entries we did not create (e.g. plain hash navigations) carry no
      // `drawolaView`; leave the current view alone rather than guessing.
      if (next === "studio" || next === "editor") {
        setView(next);
      }
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [enabled]);

  const openEditor = useCallback(() => {
    if (!enabled) {
      return;
    }
    if (readViewFromHistory() !== "editor") {
      window.history.pushState({ drawolaView: "editor" }, "");
    }
    setView("editor");
  }, [enabled]);

  const openStudio = useCallback(() => {
    if (!enabled) {
      return;
    }
    if (readViewFromHistory() === "editor") {
      // Unwind the entry `openEditor` pushed instead of growing the stack —
      // `onPopState` flips the view back to "studio".
      window.history.back();
      return;
    }
    window.history.replaceState(
      { ...(window.history.state ?? {}), drawolaView: "studio" },
      "",
    );
    setView("studio");
  }, [enabled]);

  return { view: enabled ? view : "editor", openEditor, openStudio };
};
